// shared-components/components/Buttons.jsx
import React from 'react';
import { useBreakpoint } from '../hooks/useBreakpoint';

/**
 * Unified Button System for Compudopt
 * 
 * Provides consistent button variants with standardized sizing, colors, and states.
 * All buttons use design tokens for maintainability and consistency.
 * 
 * Button Variants:
 * - PrimaryButton: Main CTAs, solid green or gradient
 * - SecondaryButton: Secondary actions, solid blue or outline
 * - GhostButton: Tertiary actions, minimal outline style
 * - QuickSelectButton: Pill-shaped buttons for conversational UI
 * - TertiaryButton: Micro-interactions, settings toggles
 */

/**
 * PrimaryButton - Main call-to-action button
 * 
 * Use for: Main CTAs, form submissions, primary actions
 * Style: Solid green (default) or gradient option
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Button content
 * @param {React.ReactNode} icon - Optional icon element
 * @param {string} size - Button size: 'small', 'medium', or 'large'
 * @param {boolean} gradient - Use gradient style instead of solid
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function PrimaryButton({ 
  children, 
  onClick, 
  disabled, 
  icon, 
  size = 'medium',
  gradient = false,
  className,
  style,
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  const sizeStyles = {
    small: { 
      padding: isMobile ? '10px 12px' : 'var(--space-2) var(--space-3)', 
      fontSize: isMobile ? '16px' : 'var(--text-xs)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    medium: { 
      padding: 'var(--space-3) var(--space-6)', 
      fontSize: 'var(--text-base)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    large: { 
      padding: 'var(--space-4) var(--space-8)', 
      fontSize: 'var(--text-lg)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{ 
        ...sizeStyles[size],
        borderRadius: 'var(--radius-pill, 25px)', // Pill shape matching compudopt.org
        fontWeight: 'var(--font-semibold)',
        color: 'var(--text-on-primary)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: disabled 
          ? 'var(--gray-500)' 
          : gradient
            ? 'var(--gradient-brand)' // Brand gradient (green to blue)
            : 'var(--gradient-green)', // Green gradient per brand guide
        boxShadow: disabled ? 'none' : 'var(--shadow-green)',
        transition: 'all var(--duration-normal) var(--ease-out)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = gradient ? 'var(--gradient-brand)' : 'var(--gradient-green-dark)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-green-intense)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = gradient ? 'var(--gradient-brand)' : 'var(--gradient-green)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-green)';
        }
      }}
      {...props}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

/**
 * SecondaryButton - Secondary action button
 * 
 * Use for: Secondary actions, cancel buttons, alternative options
 * Style: Solid blue (default) or outline option
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Button content
 * @param {React.ReactNode} icon - Optional icon element
 * @param {string} size - Button size: 'small', 'medium', or 'large'
 * @param {boolean} outline - Use outline style instead of solid
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function SecondaryButton({ 
  children, 
  onClick, 
  disabled, 
  icon, 
  size = 'medium',
  outline = false,
  className,
  style,
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  const sizeStyles = {
    small: { 
      padding: isMobile ? '10px 12px' : 'var(--space-2) var(--space-3)', 
      fontSize: isMobile ? '16px' : 'var(--text-xs)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    medium: { 
      padding: 'var(--space-3) var(--space-6)', 
      fontSize: 'var(--text-base)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    large: { 
      padding: 'var(--space-4) var(--space-8)', 
      fontSize: 'var(--text-lg)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{ 
        ...sizeStyles[size],
        borderRadius: 'var(--radius-pill, 25px)', // Pill shape matching compudopt.org
        fontWeight: 'var(--font-semibold)',
        color: outline 
          ? (disabled ? 'var(--text-disabled)' : 'var(--text-primary)')
          : 'var(--text-on-primary)',
        border: outline ? '2px solid var(--compudopt-blue)' : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: disabled 
          ? 'var(--gray-500)' 
          : outline
            ? 'var(--bg-primary)'
            : 'var(--gradient-blue)', // Blue gradient per brand guide
        boxShadow: disabled ? 'none' : (outline ? 'none' : 'var(--shadow-blue)'),
        transition: 'all var(--duration-normal) var(--ease-out)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          if (outline) {
            e.currentTarget.style.background = 'rgba(29, 118, 187, 0.05)'; // Light blue tint
            e.currentTarget.style.borderColor = 'var(--blue-dark)';
            e.currentTarget.style.color = 'var(--blue-dark)';
          } else {
            e.currentTarget.style.background = 'var(--gradient-blue-dark)';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--shadow-blue-intense)';
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          if (outline) {
            e.currentTarget.style.background = 'var(--bg-primary)';
            e.currentTarget.style.borderColor = 'var(--compudopt-blue)';
            e.currentTarget.style.color = 'var(--text-primary)';
          } else {
            e.currentTarget.style.background = 'var(--gradient-blue)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--shadow-blue)';
          }
        }
      }}
      {...props}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

/**
 * GhostButton - Minimal outline button
 * 
 * Use for: Tertiary actions, less prominent options, back/skip navigation
 * Style: Minimal outline, no background fill
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Button content
 * @param {React.ReactNode} icon - Optional icon element
 * @param {string} size - Button size: 'small', 'medium', or 'large'
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function GhostButton({ 
  children, 
  onClick, 
  disabled, 
  icon, 
  size = 'medium',
  className,
  style,
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  const sizeStyles = {
    small: { 
      padding: isMobile ? '10px 12px' : 'var(--space-2) var(--space-3)', 
      fontSize: isMobile ? '16px' : 'var(--text-xs)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    medium: { 
      padding: 'var(--space-3) var(--space-6)', 
      fontSize: 'var(--text-base)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    },
    large: { 
      padding: 'var(--space-4) var(--space-8)', 
      fontSize: 'var(--text-lg)',
      minHeight: isMobile ? 'var(--touch-target-min)' : undefined
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{ 
        ...sizeStyles[size],
        borderRadius: 'var(--radius-md)',
        fontWeight: 'var(--font-medium)',
        color: disabled ? 'var(--text-disabled)' : 'var(--text-secondary)',
        background: 'var(--bg-primary)',
        border: '2px solid var(--gray-400)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        boxShadow: 'none',
        transition: 'all var(--duration-normal) var(--ease-out)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'var(--bg-tertiary)';
          e.currentTarget.style.borderColor = 'var(--gray-500)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-primary)';
        e.currentTarget.style.borderColor = 'var(--gray-400)';
      }}
      {...props}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

/**
 * QuickSelectButton - Pill-shaped button for conversational UI
 * 
 * Use for: Quick reply options, multiple choice selections, chat action buttons,
 * language selection, explore more options, dashboard quick actions
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Button content
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function QuickSelectButton({ 
  onClick, 
  disabled, 
  children, 
  className, 
  style, 
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{
        border: '1px solid var(--border-default)',
        background: disabled ? 'var(--gray-100)' : 'var(--bg-primary)',
        borderRadius: 'var(--radius-full)', // Pill shape per brand guide
        padding: isMobile ? '6px 10px' : '4px 10px', // Chip-like compact padding
        minHeight: isMobile ? 'var(--touch-target-min)' : undefined,
        fontSize: isMobile ? '16px' : 'var(--text-xs)', // Smaller text for chip-like appearance (16px on mobile prevents iOS zoom)
        fontWeight: 'var(--font-medium)', // Medium weight per brand guide
        color: disabled ? 'var(--text-disabled)' : 'var(--text-secondary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all var(--duration-fast) var(--ease-out)',
        outline: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        gap: '4px', // Tighter gap between icon and text for chip appearance
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'var(--bg-hover)';
          e.currentTarget.style.borderColor = 'var(--compudopt-blue)'; // Brand blue per guide
          e.currentTarget.style.color = 'var(--compudopt-blue)';
          e.currentTarget.style.transform = 'scale(1.02)'; // Slight grow on hover
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'var(--bg-primary)';
          e.currentTarget.style.borderColor = 'var(--border-default)';
          e.currentTarget.style.color = 'var(--text-secondary)';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
      onMouseDown={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'scale(0.98)'; // Slight shrink on click
        }
      }}
      onMouseUp={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'scale(1.02)';
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * TertiaryButton - Subtle button for micro-interactions
 * 
 * Use for: Yes/No confirmations, settings toggles, inline actions, state indicators
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Button content
 * @param {string} variant - Visual variant: 'default', 'active', 'confirmed', or 'negative'
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function TertiaryButton({ 
  onClick, 
  disabled, 
  children, 
  variant = 'default', 
  className, 
  style, 
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  const variantStyles = {
    default: {
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-default)',
      color: 'var(--text-secondary)'
    },
    active: {
      background: 'var(--info-bg)', // Light blue
      border: '1px solid var(--compudopt-blue)',
      color: 'var(--compudopt-blue)'
    },
    confirmed: {
      background: 'var(--success-bg)', // Light green per brand guide
      border: '1px solid var(--success)',
      color: 'var(--success)'
    },
    negative: {
      background: 'var(--error-bg)', // Light red per brand guide
      border: '1px solid var(--error)',
      color: 'var(--error)'
    }
  };

  const hoverStyles = {
    default: { background: 'var(--bg-hover)', borderColor: 'var(--border-strong)' },
    active: { background: 'var(--info-bg)', borderColor: 'var(--info)' },
    confirmed: { background: 'var(--success-bg)', borderColor: 'var(--success)' },
    negative: { background: 'var(--error-bg)', borderColor: 'var(--error)' }
  };

  const baseStyle = {
    ...variantStyles[variant],
    borderRadius: 'var(--radius-md)', // 12px per brand guide
    padding: isMobile ? '12px 12px' : 'var(--space-2) var(--space-3)', // 12px vertical on mobile
    minHeight: isMobile ? 'var(--touch-target-min)' : undefined,
    fontSize: isMobile ? '16px' : 'var(--text-sm)', // 16px on mobile prevents iOS zoom
    fontWeight: 'var(--font-medium)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all var(--duration-fast) var(--ease-out)',
    opacity: disabled ? 0.5 : 1,
    outline: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={baseStyle}
      onMouseEnter={(e) => {
        if (!disabled && hoverStyles[variant]) {
          e.currentTarget.style.background = hoverStyles[variant].background;
          e.currentTarget.style.borderColor = hoverStyles[variant].borderColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = variantStyles[variant].background;
          e.currentTarget.style.borderColor = variantStyles[variant].border.split(' ')[2]; // Extract color
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * IconButton - Minimal icon button for utility actions
 * 
 * Use for: Close buttons, settings icons, minimize/maximize, menu toggles
 * 
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {React.ReactNode} children - Icon content (SVG, emoji, or text)
 * @param {string} variant - Visual variant: 'default', 'light', 'dark'
 * @param {string} size - Button size: 'small' (24px), 'medium' (32px), 'large' (40px)
 * @param {string} ariaLabel - Required ARIA label for accessibility
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function IconButton({ 
  onClick, 
  disabled, 
  children, 
  variant = 'default', 
  size = 'medium',
  ariaLabel,
  className, 
  style, 
  ...props 
}) {
  const { isMobile } = useBreakpoint();
  
  const sizeStyles = {
    small: {
      width: isMobile ? 'var(--touch-target-min)' : '36px', // Increased desktop to 36px min
      height: isMobile ? 'var(--touch-target-min)' : '36px',
      minWidth: isMobile ? 'var(--touch-target-min)' : '36px',
      minHeight: isMobile ? 'var(--touch-target-min)' : '36px',
      fontSize: 'var(--text-sm)',
      padding: '4px'
    },
    medium: {
      width: isMobile ? 'var(--touch-target-min)' : '40px', // Increased desktop slightly
      height: isMobile ? 'var(--touch-target-min)' : '40px',
      minWidth: isMobile ? 'var(--touch-target-min)' : '40px',
      minHeight: isMobile ? 'var(--touch-target-min)' : '40px',
      fontSize: 'var(--text-base)',
      padding: 'var(--space-2)'
    },
    large: {
      width: 'var(--touch-target-min)', // Always 44px or larger
      height: 'var(--touch-target-min)',
      minWidth: 'var(--touch-target-min)',
      minHeight: 'var(--touch-target-min)',
      fontSize: 'var(--text-lg)',
      padding: 'var(--space-2)'
    }
  };

  const variantStyles = {
    default: {
      background: 'transparent',
      color: 'var(--text-tertiary)', // Tertiary text per brand guide
      border: 'none'
    },
    light: {
      background: 'transparent',
      color: 'var(--text-inverse)', // White for dark backgrounds
      border: 'none'
    },
    dark: {
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      border: 'none'
    }
  };

  const hoverStyles = {
    default: { 
      background: 'var(--bg-hover)',
      color: 'var(--text-primary)'
    },
    light: { 
      background: 'rgba(255, 255, 255, 0.2)'
    },
    dark: { 
      background: 'var(--bg-hover)'
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
      style={{
        ...sizeStyles[size],
        ...variantStyles[variant],
        borderRadius: 'var(--radius-sm)', // 8px per brand guide
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all var(--duration-fast) var(--ease-out)',
        opacity: disabled ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled && hoverStyles[variant]) {
          e.currentTarget.style.background = hoverStyles[variant].background;
          if (hoverStyles[variant].color) {
            e.currentTarget.style.color = hoverStyles[variant].color;
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = variantStyles[variant].background;
          e.currentTarget.style.color = variantStyles[variant].color;
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * ToggleButton - Binary toggle button for switching between two states
 * 
 * Use for: Language switcher, view mode toggles, binary settings
 * 
 * @param {Array} options - Array of option objects: [{ value: 'en', label: 'EN' }, { value: 'es', label: 'ES' }]
 * @param {string} value - Currently selected value
 * @param {Function} onChange - Change handler, receives selected value
 * @param {boolean} disabled - Disabled state
 * @param {string} variant - Visual variant: 'default', 'light', 'dark'
 * @param {string} size - Toggle size: 'small', 'medium'
 * @param {string} ariaLabel - ARIA label for the toggle group
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function ToggleButton({ 
  options = [], 
  value, 
  onChange, 
  disabled,
  variant = 'default',
  size = 'medium',
  ariaLabel,
  className, 
  style, 
  ...props 
}) {
  const sizeStyles = {
    small: {
      padding: '2px 6px',
      fontSize: 'var(--text-2xs)',
      gap: '2px'
    },
    medium: {
      padding: '4px 8px',
      fontSize: 'var(--text-xs)',
      gap: 'var(--space-1)'
    }
  };

  const variantStyles = {
    default: {
      containerBg: 'var(--gray-100)',
      activeBg: 'var(--bg-primary)',
      activeColor: 'var(--compudopt-blue)',
      inactiveColor: 'var(--text-tertiary)',
      border: '1px solid var(--border-default)'
    },
    light: {
      containerBg: 'rgba(255, 255, 255, 0.1)',
      activeBg: 'var(--bg-primary)',
      activeColor: 'var(--compudopt-blue)',
      inactiveColor: 'var(--bg-primary)',
      border: '1px solid rgba(255, 255, 255, 0.6)'
    },
    dark: {
      containerBg: 'rgba(0, 0, 0, 0.1)',
      activeBg: 'var(--bg-primary)',
      activeColor: 'var(--compudopt-blue)',
      inactiveColor: 'var(--text-secondary)',
      border: '1px solid var(--border-default)'
    }
  };

  const variantStyle = variantStyles[variant];

  return (
    <div
      role="group"
      aria-label={ariaLabel || 'Toggle options'}
      className={className}
      style={{
        display: 'flex',
        ...sizeStyles[size],
        background: variantStyle.containerBg,
        border: variantStyle.border,
        borderRadius: 'var(--radius-full)',
        ...style
      }}
      {...props}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            onClick={() => !disabled && onChange && onChange(option.value)}
            disabled={disabled}
            aria-pressed={isActive}
            style={{
              background: isActive ? variantStyle.activeBg : 'transparent',
              color: isActive ? variantStyle.activeColor : variantStyle.inactiveColor,
              border: 'none',
              borderRadius: 'var(--radius-full)',
              padding: size === 'small' ? '2px 8px' : '4px 8px',
              fontSize: sizeStyles[size].fontSize,
              fontWeight: 'var(--font-bold)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all var(--duration-fast) var(--ease-out)',
              opacity: disabled ? 0.5 : 1,
              minWidth: size === 'small' ? '28px' : '32px'
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}